import { Coordinate, CoordinateBounds } from "../hooks/use-coordinates";
import { LngLat } from "mapbox-gl";
import {
  LineStyle,
  PointStyle,
  LabelDecoration,
  Point,
  Size,
} from "./poster-types";
import { Thumbnail as MapStyleThumbnail } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-thumbnail-grid";
import {
  EditingPermissions,
  LabelEditingPermissions,
  BackdropEditingPermissions,
  LineEditingPermissions,
  BorderEditingPermissions,
  PointEditingPermissions,
} from "./editing-permissions";

export const PosterProjectCurrentModelVersion = 11;

export interface ProjectStore {
  projects: PosterProject[];
}

export interface GPSSource {
  id: string;
  name?: string;
  originDescription: string; //File name or other source.
  allCoordinates: Coordinate[];
  displayedCoordinates: Coordinate[];
  coordinateLimit?: number;
}

export interface PosterProject {
  id: string;
  name: string;
  modelVersion: number;
  thumbnail?: string;
  createdAt: Date;
  updatedAt: Date;

  //ID of the last rendered canvas, to prevent caching (if using the same name like
  //"canvas".  Tracked so we can delete the old one when we make a new map render.
  lastRenderID: string | undefined;
  isRendered: boolean; //Indicates if the project has been rendered or not.

  //Global editing permissions for the project.
  //Each element such as map, labels, etc. can have its own editing permissions.
  //They are populated based on the template the project was generated from.
  editingPermissions: EditingPermissions;

  //Indicates whether the project has just been created from a template and the user
  //hasn't yet decided to edit/commit to it and save it as a new project.
  //If this is true, can be considered discardable.
  isTemplateDraft: boolean;

  gpsSourceIDs: string[]; //IDs of the GPS sources used in this project.

  // STYLES APPLIED TO ADDED ELEMENTS BY DEFAULT/PRESET.
  // Individual elements have their own styles, taken from these presets
  // initially and possibly modified by the user.
  legendStyle: PosterLegendStyle;
  pointStyle: PointStyle;
  lineStyle: LineStyle;
  borderStyle: BorderStyle;
  backdropStyle: BackdropStyle;

  mapLabelStyle: MapLabelStyle;
  staticLabelStyle: StaticLabelStyle;

  size: PosterSizePreset;
  mapStyle: string; // Actual map style from map box.

  bounds?: CoordinateBounds;

  legend: PosterLegend;

  styledPoints: StyledPoint[];
  styledPointsUpdateKey?: string; // Used to force a rerender of the styled points.

  mapLabels: MapLabel[];
  staticLabels: StaticLabel[];
  labelUpdateKey?: string; // Used to force a rerender of the labels.

  backdrops: Backdrop[];
  backdropUpdateKey?: string; // Used to force a rerender of the backdrops.
}

export interface BorderStyle {
  borderWidth?: number; // In MM.
  borderColor?: string;
}

export type PosterLegendPosition = "bottom" | "top" | "left" | "right";

export interface PosterLegend {
  labels: string[];
  style: PosterLegendStyle;
}

export interface PosterLegendStyle {
  visible: boolean;
  labelColor: string;
  backgroundColor: string;
  backgroundOpacity: number;
  position: PosterLegendPosition;
  labelTemplate: PosterLegendLableTemplate;
}

// These define where the labels go, their font size, and their color, not the legend's backgorund
// I've created a component for each of these layouts, which combined with `orientation`
// will determine the final layout of the legend.
// E.g. layout_03 might have classic styled labels for title, two subtitles, and a third label at the bottom
// with a sans serif font (positioned differently for landscape and portrait).
export type PosterLegendLableTemplate = "layout_01" | "layout_02" | "layout_03";

// Determines how a label grows as text is entered.
// e.g. if you align a lable to the right side of the poster, you want it to grow/shrink
// to the left. Otherwise presets would get messed up when you start entering your own text.
export type TextAlignment = "left" | "center" | "right";

export type DataVisualization = "none" | "elevation" | "speed";

export interface StyledPoint {
  coordinate: Coordinate;
  style: PointStyle;
  editingPermissions?: PointEditingPermissions;
}

export interface Backdrop {
  id: string;
  style: BackdropStyle;
  position: Point;

  name: string;
  //For backdrops, size.width is RATIO the width.
  //This is so they scale with map width (which affects font sizes).
  size: Size;

  editingPermissions?: BackdropEditingPermissions;

  dataVisualation?: DataVisualization;
}

export interface BackdropStyle {
  backgroundColor: string;
  opacity: number;
  borderWidth: number;
  borderColor: string;
  cornerRadius: number;
}

export interface MapLabel {
  id: string;
  coordinate: Coordinate;
  text: string;
  style: MapLabelStyle;
  editingPermissions?: LabelEditingPermissions;
}

export interface MapLabelStyle {
  backgroundColor: string;
  backgroundOpacity: number;
  padding?: number;
  decorationStyle: MapLabelDecorationStyle;
  borderRadius: number;
  pointerColor: string;
  pointerThickness: number;
  font: string;
  textColor: string;
  fontSize: number;
  offset: { x: number; y: number };
}

export interface StaticLabel {
  id: string;
  description: string;
  text: string;
  position: Point;
  style: StaticLabelStyle;
  editingPermissions?: LabelEditingPermissions;
}

export interface StaticLabelStyle {
  alignment: TextAlignment;
  backgroundColor: string;
  backgroundOpacity: number;
  padding: number;
  borderRadius: number;
  font: string;
  textColor: string;
  fontSize: number;
}

export interface MapLabelDecorationStyle {
  decoration: LabelDecoration;
  width: number; //% not px.
  length: number; //% not px.
  color: string;
}

export const defaultCoordinateLimit = 5000;

export type UnitMeasurement = "inches" | "cm";
export const AvailableUnitMeasurements: UnitMeasurement[] = ["inches", "cm"];

export type PosterSizePreset =
  | "12x12"
  | "12x20"
  | "20x12"
  | "20x20"
  | "18x20"
  | "20x18";

export const AvailableSizePresets: PosterSizePreset[] = [
  "12x20",
  "12x12",
  "20x12",
  "20x20",
  "18x20",
  "20x18",
];

export const cssAspectRatioFromPreset = (preset: PosterSizePreset) => {
  switch (preset) {
    case "12x12":
      return "12 / 12";
    case "20x12":
      return "20 / 12";
    case "12x20":
      return "12 / 20";
    case "20x20":
      return "20 / 20";
    case "18x20":
      return "18 / 20";
    case "20x18":
      return "20 / 18";
    default:
      return "12 / 12";
  }
};

export const sizeInInchesFromPreset = (
  preset: PosterSizePreset
): { width: number; height: number } => {
  switch (preset) {
    case "12x12":
      return { width: 12, height: 12 };
    case "12x20":
      return { width: 12, height: 20 };
    case "18x20":
      return { width: 18, height: 20 };
    case "20x12":
      return { width: 20, height: 12 };
    case "20x20":
      return { width: 20, height: 20 };
    case "20x18":
      return { width: 20, height: 18 };
  }
};

export const incrementSizeInMM = 5;
export const incrementSizeInPX = (preset: PosterSizePreset) => {
  const screenSize = screenSizeFromPreset(preset);
  const sizeInCM = sizeInCMFromPreset(preset);
  const divisions = sizeInCM.width / 0.5; // 0.5 cm increments
  const divisionLength = screenSize.width / divisions;
  return divisionLength;
};

export const nearestIncrementValueToValue = (
  value: number,
  incrementSizeInPX: number
) => {
  return Math.round(value / incrementSizeInPX) * incrementSizeInPX;
};

export const sizeInCMFromPreset = (
  preset: PosterSizePreset
): { width: number; height: number } => {
  switch (preset) {
    case "12x12":
      return { width: 30, height: 30 };
    case "12x20":
      return { width: 30, height: 51 };
    case "18x20":
      return { width: 46, height: 51 };
    case "20x12":
      return { width: 51, height: 30 };
    case "20x20":
      return { width: 51, height: 51 };
    case "20x18":
      return { width: 51, height: 46 };
  }
};

export const aspectRatioFromPreset = (preset: PosterSizePreset) => {
  const sizeInCM = sizeInCMFromPreset(preset);
  return sizeInCM.width / sizeInCM.height;
};

export const screenSizeFromPreset = (
  preset: PosterSizePreset
): { width: number; height: number } => {
  const aspectRatio = aspectRatioFromPreset(preset);
  const largest = 800;
  if (aspectRatio > 1) {
    return { width: largest, height: largest / aspectRatio };
  } else {
    return { width: largest * aspectRatio, height: largest };
  }
};

export const fullRenderSizeFromPreset = (
  preset: PosterSizePreset
): { width: number; height: number } => {
  const largest = 1280;
  const aspectRatio = aspectRatioFromPreset(preset);
  let size: Size;
  if (aspectRatio > 1) {
    size = { width: largest, height: largest / aspectRatio };
  } else {
    size = { width: largest * aspectRatio, height: largest };
  }
  return size;
};
export const mapRenderSizeFromPreset = (
  preset: PosterSizePreset,
  borderWidthMM: number
): { width: number; height: number } => {
  const size = fullRenderSizeFromPreset(preset);
  const borderRatios = borderRatioToPosterSize(borderWidthMM, preset);
  const borderThickness = size.height * borderRatios.vertical;
  const width = Math.round(size.width - borderThickness);
  const height = Math.round(size.height - borderThickness);
  return { width: width, height: height };
};

export const fullThumbnailSizeFromPreset = (
  preset: PosterSizePreset
): { width: number; height: number } => {
  const renderSize = fullRenderSizeFromPreset(preset);
  const scale = 0.3;
  return {
    width: Math.floor(renderSize.width * scale),
    height: Math.floor(renderSize.height * scale),
  };
};

export const mapThumbnailSizeFromPreset = (
  preset: PosterSizePreset,
  borderWidthMM: number
): { width: number; height: number } => {
  const renderSize = mapRenderSizeFromPreset(preset, borderWidthMM);
  const scale = 0.3;
  return {
    width: Math.floor(renderSize.width * scale),
    height: Math.floor(renderSize.height * scale),
  };
};

export interface MapStaticImageOptions {
  northeast: LngLat;
  southwest: LngLat;
  mapStyle: string;
  renderSizePreset: PosterSizePreset;
  borderWidthMM: number;
  isThumbnail: boolean;
}

export interface MapStyle {
  mapBoxStyleID: string;
  styleURL: string;
}

export const MapStyleBaseURL = "mapbox://styles/jamesbkelly/";

export const generateMapStyles = async () => {
  const style = await mapStyleFromManifest();
  const mapStyles: MapStyle[] = [];
  style.styles.forEach((style: any) => {
    let id = style.styleId;
    mapStyles.push({
      mapBoxStyleID: id,
      styleURL: `${MapStyleBaseURL}${id}`,
    });
  });
  return mapStyles;
};

const mapStyleFromManifest = async () => {
  const path = "/pallete-components/map-thumbnails/map-style-manifest.json";
  let response = await fetch(path, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
  let data = await response.json();
  return data;
};

export const lineColors = [
  "#FFFFFF",
  "#F7F7F7",
  "#DCDCDC",
  "#2A2A2A",
  "#6B6B6B",
  "#ECADC4",
  "#F9357C",
  "#EE2525",
  "#FEB237",
  "#FEDD37",
  "#EDFE37",
  "#DFF8E3",
  "#EEFAE3",
  "#6CD888",
  "#22BA52",
  "#ADECE4",
  "#DAF7F3",
  "#FF4FBB",
  "#ED1EDB",
  "#D172F7",
  "#005AB4",
  "#4E13C3",
  "#5910EB",
  "#6E2CF1",
];

export const legendTintColors = [
  "#FFFFFF",
  "#F7F7F7",
  "#DCDCDC",
  "#6B6B6B",
  "#2A2A2A",
  "#000000",
  "#ECADC4",
  "#F9357C",
  "#EE2525",
  "#FEB237",
  "#FEDD37",
  "#EDFE37",
  "#DFF8E3",
  "#EEFAE3",
  "#6CD888",
  "#22BA52",
  "#ADECE4",
  "#DAF7F3",
  "#FF4FBB",
  "#ED1EDB",
  "#D172F7",
  "#005AB4",
  "#4E13C3",
  "#5910EB",
  "#6E2CF1",
];

export const fontNames = (): string[] => {
  return [
    "AlfaSlabOne",
    "Anton",
    "Archivo",
    "BigShouldersInlineText",
    "BlackOpsOne",
    "BungeeHairline",
    "BungeeShade",
    "Dosis",
    "FrancoisOne",
    "FrankRuhlLibre",
    "FrederickatheGreat",
    "Fredoka",
    "FugazOne",
    "GasoekOne",
    "GolosText",
    "GravitasOne",
    "Gugi",
    "JacquesFrancoisShadow",
    "Kanit",
    "Karla",
    "KodeMono",
    "LeagueGothic",
    "LexendTera",
    "LibreBarcode39Text",
    "LibreBaskerville",
    "LondrinaShadow",
    "MPLUS1Code",
    "MajorMonoDisplay",
    "MoiraiOne",
    "Monoton",
    "NotoSansKR",
    "NotoSerifHentaigana",
    "NovaMono",
    "Oswald",
    "PTMono",
    "Pacifico",
    "PaytoneOne",
    "PlaywriteAUSA",
    "PlaywriteITModerna",
    "PressStart2P",
    "ProtestGuerrilla",
    "Quicksand",
    "RalewayDots",
    "ReenieBeanie",
    "Rock3D",
    "RockSalt",
    "Rowdies",
    "RubikDirt",
    "RubikDoodleShadow",
    "RubikMaps",
    "RubikMicrobe",
    "ScheherazadeNew",
    "ShadowsIntoLight",
    "ShareTech",
    "Shizuru",
    "Slackey",
    "SmoochSans",
    "SpecialElite",
    "Teko",
    "TextMeOne",
    "Tourney",
    "VT323",
    "Zeyada",
  ];
};

export const borderWidthInPixels = (
  borderWidthInMM: number,
  posterSize: PosterSizePreset
) => {
  const width = sizeInCMFromPreset(posterSize).width;
  const posterWidthInMM = width * 2.54 * 10;
  const percentage = Math.abs(borderWidthInMM / posterWidthInMM);
  const posterPixelSize = screenSizeFromPreset(posterSize).width;
  const borderPixelWidth = posterPixelSize * percentage;
  return borderPixelWidth;
};

export const borderRatioToPosterSize = (
  borderWidthInMM: number,
  posterSize: PosterSizePreset
): { horizontal: number; vertical: number } => {
  const width = sizeInCMFromPreset(posterSize).width;
  const height = sizeInCMFromPreset(posterSize).height;

  const posterWidthInMM = width * 10;
  const posterHeightInMM = height * 10;

  const ratioWidth = Math.abs(borderWidthInMM / posterWidthInMM);
  const ratioHeight = Math.abs(borderWidthInMM / posterHeightInMM);

  return { horizontal: ratioWidth, vertical: ratioHeight };
};

const generateThumbnails = async () => {
  const mapStyles = await generateMapStyles();
  const thumbnails: MapStyleThumbnail[] = [];
  for (const style of mapStyles) {
    thumbnails.push({
      id: style.mapBoxStyleID,
      src: `/pallete-components/map-thumbnails/${style.mapBoxStyleID}.png`,
      alt: `Map style ${style.mapBoxStyleID}`,
    });
  }
  return thumbnails;
};
